import * as React from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { Button, Modal, ModalContent } from '@restworld/ui-ds-base';
import { useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { useAnalytics, useKv } from '@restworld/utils-common';

import {
  referencesReqSelectorModalVisibleAtom,
  useListWorkerPastExperiencesMutation,
} from './references-request-selector-modal';
import { getWorkerAtom, workerPastExperiencesAtom, workerPersonalDetailsAtom } from '../global-state.worker-data';
import { referencesRequestSuccessMessageVisibleAtom } from './references-request-message-modal';
import { useFetchWorkerDetailsQueryKey } from '../../hooks';
import { alreadyConfirmedConsentAtom, isReferencesContentGeneralFlowAtom } from './profile-details-section';

export const referencesConsentModalVisibleAtom = atom<boolean>(false);
export const referencesModalClassName = 'max-w-lg lg:max-w-2xl p-2 bg-neutral-10 bg-gray-50';

export const useUpdateWorkerConsentMutation = () => {
  const workerPastExperiences = useAtomValue(workerPastExperiencesAtom);
  const setReferencesReqSelectorModalVisible = useSetAtom(referencesReqSelectorModalVisibleAtom);
  const setReferencesConsentModalVisible = useSetAtom(referencesConsentModalVisibleAtom);
  const setReferencesRequestSuccessMessageVisible = useSetAtom(referencesRequestSuccessMessageVisibleAtom);
  const workerService = useWorkersService();
  const workerId = useAuth().user?.id;
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const didWorkerUpdateExperience = router.asPath.includes('#added_past_experiences');
  const queryClient = useQueryClient();
  const workerDetailsQueryKey = useFetchWorkerDetailsQueryKey();

  return useMutation({
    mutationFn: async ({
      consentToCollectReferences,
      referencesInformationModalLastShownAt,
    }: {
      consentToCollectReferences: boolean;
      referencesInformationModalLastShownAt?: string;
    }) => {
      return (
        await workerService.updateWorker({
          workerId,
          params: {
            consent_to_collect_references: consentToCollectReferences,
            references_information_modal_last_shown_at: referencesInformationModalLastShownAt,
          },
        })
      ).data;
    },
    onSuccess: (data, params) => {
      setReferencesConsentModalVisible(false);
      if (didWorkerUpdateExperience && params?.consentToCollectReferences) {
        setReferencesRequestSuccessMessageVisible(true);
        router.push('/profilo', undefined, {
          scroll: false,
          shallow: true,
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: workerDetailsQueryKey });
      if (workerPastExperiences?.length > 0 && params?.consentToCollectReferences) {
        setReferencesReqSelectorModalVisible(true);
        trackEvent('worker_enables_references', {});
      }
    },
    onError: (error) => {
      console.error('Error updating worker data:', error);
    },
  });
};
export const useCheckIfUserHasDeclinedConsentRequest = () => {
  const referencesInformationModalLastShownAt =
    useAtomValue(workerPersonalDetailsAtom)?.referencesInformationModalLastShownAt;
  const consentToCollectReferences = useAtomValue(workerPersonalDetailsAtom)?.consentToCollectReferences;
  const workerPastExperiences = useAtomValue(workerPastExperiencesAtom);

  const { setKV, getKV } = useKv();
  const workerId = useAuth().user?.id;
  const declineConsentCountKey = React.useMemo(() => `worker_decline_consent_count_${workerId}`, [workerId]);

  // Check if the last decline was more than a month ago

  const declinedMoreThanMonthAgo = React.useMemo(() => {
    if (!referencesInformationModalLastShownAt) return false;
    const lastDate = new Date(referencesInformationModalLastShownAt);
    const now = new Date();
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(now.getMonth() - 1);
    return lastDate < oneMonthAgo;
  }, [referencesInformationModalLastShownAt]);

  // Get decline count from KV
  const getDeclineCountFromKV = React.useCallback(async () => {
    const res = await getKV(declineConsentCountKey);
    return res?.value ? Number(res.value) : 0;
  }, [getKV, declineConsentCountKey]);

  // Set decline count in KV
  const setDeclineCountInKV = React.useCallback(
    async (count?: number) => {
      const currentCount = await getDeclineCountFromKV();
      await setKV({
        key: declineConsentCountKey,
        value: typeof count === 'number' ? count : declinedMoreThanMonthAgo ? 100 : currentCount + 1,
      });
    },
    [getDeclineCountFromKV, setKV, declineConsentCountKey, declinedMoreThanMonthAgo]
  );

  // Determine if modal should be shown
  const showDeclineConsentModal = React.useCallback(async () => {
    const declineCount = await getDeclineCountFromKV();

    // Don't show if no past experiences or already consented or max declines reached
    if (!workerPastExperiences?.length || consentToCollectReferences || declineCount === 100) {
      return false;
    }

    // Show if user has declined less than 2 times
    if (declineCount < 2) {
      return true;
    }

    // Show again if user declined more than 2 times but last decline was over a month ago
    if (declineCount >= 2 && declinedMoreThanMonthAgo) {
      return true;
    }

    // Otherwise, don't show
    return false;
  }, [getDeclineCountFromKV, workerPastExperiences?.length, consentToCollectReferences, declinedMoreThanMonthAgo]);

  return {
    setDeclineCountInKV,
    getDeclineCountFromKV,
    showDeclineConsentModal,
  };
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IReferencesConsentModalProps {}
export const ReferencesConsentModal: React.FunctionComponent<IReferencesConsentModalProps> = (props) => {
  const { t } = useTranslation('worker-references-page');
  const [isVisible, setIsVisible] = useAtom(referencesConsentModalVisibleAtom);
  const workerPastExperiences = useAtomValue(workerPastExperiencesAtom);
  const { data: workerData } = useAtomValue(getWorkerAtom);
  const setIsReferencesContentGeneralFlow = useSetAtom(isReferencesContentGeneralFlowAtom);
  const setAlreadyConfirmedConsent = useSetAtom(alreadyConfirmedConsentAtom);

  const { mutate: updateWorkerConsent, isPending } = useUpdateWorkerConsentMutation();
  const { setDeclineCountInKV } = useCheckIfUserHasDeclinedConsentRequest();

  const {
    mutate: updatePastExperience,
    isSuccess: updateWorkerPastExperienceSuccess,
    reset: resetWorkerPastExperience,
    isPending: isUpdatingPastExperiencePending,
  } = useListWorkerPastExperiencesMutation();

  const isNewExperienceAdded = useRouter().asPath.includes('#added_past_experience');

  const recentAddedExperience = React.useMemo(() => {
    // checks if any experience was added in the last 2 minutes
    return workerPastExperiences?.find(
      (experience) => experience.inserted_at > new Date(Date.now() - 2 * 60 * 1000).toISOString()
    );
  }, [workerPastExperiences]);

  const consentToCollectReferencesRecorded = React.useMemo(
    () => workerData?.consent_to_collect_references,
    [workerData?.consent_to_collect_references]
  );

  React.useEffect(() => {
    if (updateWorkerPastExperienceSuccess) {
      setIsVisible(false);
      resetWorkerPastExperience();
    }
  }, [resetWorkerPastExperience, setIsVisible, updateWorkerPastExperienceSuccess]);

  return (
    <Modal open={isVisible} onOpenChange={(v) => !v && setIsVisible(false)}>
      <ModalContent
        noPadding
        className={referencesModalClassName}
        title={
          <h5 className="px-4 lg:px-[40px] self-stretch text-center text-neutral-700 text-lg font-semibold">
            {isNewExperienceAdded ? t('references_consent_modal.title_cond') : t('references_consent_modal.title')}
          </h5>
        }
        footer={
          <div className="px-4 lg:px-[40px] w-full border-t border-neutral-40 flex flex-col justify-start gap-4 py-4">
            <Button
              fullwidth
              variant="primary"
              onClick={() => {
                if (!consentToCollectReferencesRecorded)
                  updateWorkerConsent({
                    consentToCollectReferences: true,
                  });
                if (isNewExperienceAdded)
                  updatePastExperience([
                    {
                      id: recentAddedExperience?.id,
                      collect_references: true,
                    },
                  ]);
              }}
              loading={isPending || isUpdatingPastExperiencePending}
            >
              {t('references_consent_modal.footer.button.primary')}
            </Button>
            <Button
              className={'shadow-none'}
              variant="tertiary"
              fullwidth
              onClick={() => {
                if (!isNewExperienceAdded) {
                  updateWorkerConsent({
                    consentToCollectReferences: false,
                    referencesInformationModalLastShownAt: new Date().toISOString(),
                  });
                  setDeclineCountInKV();
                  setIsReferencesContentGeneralFlow(false);
                  setAlreadyConfirmedConsent(true);
                }
                setIsVisible(false);
              }}
            >
              {t('references_consent_modal.footer.button.secondary')}
            </Button>
          </div>
        }
      >
        <div className="py-9 px-4 lg:px-[40px] w-full inline-flex flex-col justify-center items-center gap-8 text-center">
          <div className="flex flex-col justify-start gap-2">
            <h5 className="text-neutral-700 text-lg font-semibold">
              {t('references_consent_modal.consent.fast_and_secure')}
            </h5>
            <h5 className="text-neutral-700 text-base font-normal">
              {t('references_consent_modal.consent.choose_references')}
            </h5>
          </div>
          <div className="flex flex-col gap-2">
            <h5 className="text-neutral-700 text-lg font-semibold">
              {t('references_consent_modal.consent.full_control')}
            </h5>
            <h5 className="text-neutral-700 text-base font-normal">
              {t('references_consent_modal.consent.preview_references')}
            </h5>
          </div>
          <div className="flex flex-col gap-2">
            <h5 className="text-neutral-700 text-lg font-semibold">
              {t('references_consent_modal.consent.double_chances')}
            </h5>
            <h5 className="text-neutral-700 text-base font-normal">
              {t('references_consent_modal.consent.profiles_with_references')}
            </h5>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ReferencesConsentModal;
